import { ApiClient, PushNotificationsClient } from "./clients";
import { User, Client, UserConfig, Field, Group } from "@/types";
import { UserMobileStorageEntity } from "@/types/debugging";
import { userMobileDataFromStore } from "@/utils/debugging/userMobileDataFromStore";
import { localLogger } from "@/utils/localLogger";

export const UserService = {
  async me(): Promise<User | undefined> {
    const data = await ApiClient.get<User>("/api/v1/users/me/");
    if (!data) return undefined;
    return {
      id: data.id,
      future_user_id: data.future_user_id,
      future_organization_id: data.future_organization_id,
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      favorite_teams: data.favorite_teams,
      calendar_url: data.calendar_url,
      intercom_user_hash: data.intercom_user_hash,
      config: data.config,
      followed_entities: data.followed_entities,
      in_app_notifications_whitelist: data.in_app_notifications_whitelist,
      language: data.language,
      profile: data.profile,
      is_active: true,
      is_client_admin: data.is_client_admin,
    };
  },
  async getClient(): Promise<Client> {
    type ClientV2 = Omit<Client, "extra_fields" | "groups"> & {
      groups: Omit<Group, "extra_fields">[];
    };
    const [client, extra_fields_except_zones, zones] = await Promise.all([
      ApiClient.get<ClientV2>("/api/v2/user/current/client/"),
      ApiClient.get<Field[]>("/api/v1/properties/fields/"),
      ApiClient.get<Field[]>("/api/v1/zones"),
    ]);
    const extra_fields = extra_fields_except_zones?.concat(zones ?? []);
    if (!client || !extra_fields || !zones) {
      const missing = [
        !client && "client",
        !extra_fields && "extra_fields",
        !zones && "zones",
      ].filter(Boolean);
      throw new Error("client, zone and extra fields are required", {
        cause: { missing },
      });
    }
    return Object.assign(client, {
      extra_fields: extra_fields.filter(
        // Priority is the only extra field that is defined at the organization (=client) level so the condition below needs
        // requires to also check if the field.type == "priority" because in that case it may not be in field.groups.
        (field) => field.groups.length === 0 || field.type === "priority"
      ),
      groups: client.groups.map((group: Omit<Group, "extra_fields">) => ({
        ...group,
        extra_fields: extra_fields.filter((field: Field) =>
          field.groups.includes(group.id)
        ),
      })),
    });
  },
  async saveConfig(config: UserConfig): Promise<User | undefined> {
    const user = await ApiClient.put<User>(`/api/v1/users/me/`, { config });
    return user ?? undefined;
  },
  async setPushNotificationToken(deviceId: any, device: any): Promise<void> {
    try {
      await PushNotificationsClient.post("/devices", {
        deviceId,
        device,
      });
    } catch (e) {
      await localLogger.log(`ERROR setPushNotificationToken NEW SERVICE, ${e}`);
    }
  },
  async removePushNotificationToken(deviceToken: any) {
    try {
      await PushNotificationsClient.delete(`/devices/${deviceToken}`);
    } catch (e) {
      console.error("removePushNotificationToken", e);
    }
  },
  async getDiamondUserMapping() {
    const users = await ApiClient.get<
      {
        future_user_id: string;
        user_id: number;
      }[]
    >(`/api/v1/user/current/client/diamond-user-mapping/`);
    return users ?? [];
  },
  async sendUserMobileData() {
    try {
      const data = (await ApiClient.get("/debugging/user-mobile-data/")) as
        | {
            id: number;
            entities: UserMobileStorageEntity[];
          }
        | string;
      if (!data || data === "" || typeof data === "string") {
        return;
      }
      const dataToSend = await userMobileDataFromStore(data.entities);
      await ApiClient.put("/debugging/user-mobile-data/export/", {
        id: data.id,
        data: dataToSend,
      });
    } catch (e) {
      await localLogger.log(`sendUserMobileData, ${e}`);
    }
  },
};
